import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { initializeApp, getApps } from 'firebase/app'
import {
    getDatabase, ref, onValue, set, get, push, off, update
} from 'firebase/database'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { tournamentsAPI, getMongoId } from '../api/api'

// ─── Firebase ─────────────────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
    apiKey:            "AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc",
    authDomain:        "clubswap-5a74a.firebaseapp.com",
    databaseURL:       "https://clubswap-5a74a-default-rtdb.firebaseio.com",
    projectId:         "clubswap-5a74a",
    storageBucket:     "clubswap-5a74a.firebasestorage.app",
    messagingSenderId: "274838532963",
    appId:             "1:274838532963:web:4044ba76f6192dcfdf5956",
}

let _app, _db, _auth
function getDB() {
    if (!_db) {
        _app  = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG)
        _db   = getDatabase(_app)
        _auth = getAuth(_app)
    }
    return _db
}
function getFirebaseAuth() { getDB(); return _auth }

// ─── Bot API ──────────────────────────────────────────────────────────────────
const BOT_API = 'http://localhost:3001'
async function notifyUser(uid, message) {
    try {
        await fetch(`${BOT_API}/api/notify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid, message })
        })
    } catch (e) { console.warn('Bot notify failed:', e.message) }
}
async function notifyAll(uids, message) {
    await Promise.all(uids.map(uid => notifyUser(uid, message)))
}

// ─── Constants ────────────────────────────────────────────────────────────────
const PLATFORM_FEE    = 0.15   // 15%
const HOST_BONUS_PCT  = 0.05   // 5% of gross, paid by platform
const WAITING_MS      = 15 * 60 * 1000   // 15 min to fill lobby
const PROGRESS_MS     = 25 * 60 * 1000   // 25 min tournament window
const PAYOUT_DELAY_MS =  5 * 60 * 1000   // 5 min after winners announced
const DISPUTE_PENALTY = 10

// ─── Prize calc ───────────────────────────────────────────────────────────────
function calcPrizes(totalPot, prizeMode, playerCount) {
    const net = Math.floor(totalPot * (1 - PLATFORM_FEE))
    if (prizeMode === 'winner') {
        return { 1: net }
    }
    // top3: 50/30/20
    return {
        1: Math.floor(net * 0.50),
        2: Math.floor(net * 0.30),
        3: Math.floor(net * 0.20),
    }
}

function hostBonus(entryFee, playerCount) {
    return playerCount === 8 ? Math.floor(entryFee * playerCount * HOST_BONUS_PCT) : 0
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtTime(ms) {
    if (ms <= 0) return '0:00'
    const s = Math.floor(ms / 1000)
    const m = Math.floor(s / 60)
    return `${m}:${String(s % 60).padStart(2, '0')}`
}
function initials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}
function posLabel(n) {
    return n === 1 ? '🥇 1st' : n === 2 ? '🥈 2nd' : n === 3 ? '🥉 3rd' : 'DNP'
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function TournamentLobby() {
    const { tid }  = useParams()
    const navigate = useNavigate()

    const [currentUser, setCurrentUser] = useState(null)
    const [authReady, setAuthReady]     = useState(false)
    const [tournament, setTournament]   = useState(null)
    const [mongoTournament, setMongoTournament] = useState(null)
    const [players, setPlayers]         = useState({})   // uid → playerData
    const [loading, setLoading]         = useState(true)
    const [error, setError]             = useState('')
    const [actionLoading, setActionLoading] = useState(false)
    const [copiedKey, setCopiedKey] = useState(null)

    // Timers
    const [waitTimeLeft, setWaitTimeLeft]     = useState(null)
    const [progressTimeLeft, setProgressTimeLeft] = useState(null)
    const [payoutTimeLeft, setPayoutTimeLeft] = useState(null)

    // Result claiming
    const [myClaim, setMyClaim]         = useState(null)   // 1 | 2 | 3 | 'dnp'
    const [claimSubmitted, setClaimSubmitted] = useState(false)
    const [hostRankings, setHostRankings] = useState({ 1: '', 2: '', 3: '' })
    const [showHostDeclare, setShowHostDeclare] = useState(false)
    const [payoutCountdown, setPayoutCountdown] = useState(null)

    const timerRef = useRef(null)

    // Dispute window (20 min after first claim submitted)
    const [disputeTimeLeft, setDisputeTimeLeft]     = useState(null)
    const [disputeWindowOpen, setDisputeWindowOpen] = useState(false)
    const [showDisputeModal, setShowDisputeModal]   = useState(false)
    const [disputeDepositing, setDisputeDepositing] = useState(false)
    const disputeTimerRef = useRef(null)

    // Match timer (15 min playing window before claims unlock)
    const [matchTimerDone, setMatchTimerDone]   = useState(false)
    const matchTimerRef = useRef(null)

    // ── Auth ──────────────────────────────────────────────────
    useEffect(() => {
        const auth = getFirebaseAuth()
        const unsub = onAuthStateChanged(auth, user => {
            setCurrentUser(user)
            setAuthReady(true)
        })
        return () => unsub()
    }, [])

    // ── Listen to tournament ──────────────────────────────────
    useEffect(() => {
        if (!tid) return
        const db = getDB()
        const tRef = ref(db, `tournaments/${tid}`)
        const unsub = onValue(tRef, snap => {
            if (!snap.exists()) { setError('Tournament not found.'); setLoading(false); return }
            const data = snap.val()
            setTournament(data.meta)
            setPlayers(data.players || {})
            setLoading(false)

            // Check my claim
            if (currentUser && data.players?.[currentUser.uid]?.claim !== undefined) {
                setMyClaim(data.players[currentUser.uid].claim)
                setClaimSubmitted(true)
            }
        })
        return () => off(tRef)
    }, [tid, currentUser])

    // ── MongoDB fallback when Firebase meta is missing ────────
    // Handles tournaments created before backend Firebase sync was added
    useEffect(() => {
        if (!loading && !error && !tournament && tid) {
            tournamentsAPI.get(tid)
                .then(setMongoTournament)
                .catch(() => setError('Tournament not found.'))
        }
    }, [loading, error, tournament, tid])

    // ── Waiting timer (lobby open) ────────────────────────────
    useEffect(() => {
        if (!tournament || tournament.status !== 'waiting') return
        const tick = () => {
            const left = (tournament.createdAt + WAITING_MS) - Date.now()
            setWaitTimeLeft(Math.max(0, left))
            if (left <= 0) handleWaitExpiry()
        }
        tick()
        timerRef.current = setInterval(tick, 1000)
        return () => clearInterval(timerRef.current)
    }, [tournament?.status, tournament?.createdAt])

    // ── Progress timer (tournament running) ───────────────────
    useEffect(() => {
        if (!tournament || tournament.status !== 'inprogress') return
        const tick = () => {
            const left = (tournament.startedAt + PROGRESS_MS) - Date.now()
            setProgressTimeLeft(Math.max(0, left))
        }
        tick()
        const t = setInterval(tick, 1000)
        return () => clearInterval(t)
    }, [tournament?.status, tournament?.startedAt])

    // ── Payout countdown (after winners announced) ────────────
    useEffect(() => {
        if (!tournament || tournament.status !== 'announced') return
        const tick = () => {
            const left = (tournament.announcedAt + PAYOUT_DELAY_MS) - Date.now()
            setPayoutTimeLeft(Math.max(0, left))
            if (left <= 0) handleAutoSettle()
        }
        tick()
        const t = setInterval(tick, 1000)
        return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tournament?.status, tournament?.announcedAt])

    // ── 15-min match timer ───────────────────────────────────
    useEffect(() => {
        if (!tournament || tournament.status !== 'inprogress') {
            clearInterval(matchTimerRef.current)
            return
        }
        const startedAt = tournament.startedAt
        if (!startedAt) return  // wait until startedAt is set in Firebase
        const deadline  = startedAt + 15 * 60 * 1000

        const tick = () => {
            const left = deadline - Date.now()
            if (left <= 0) {
                clearInterval(matchTimerRef.current)
                setMatchTimerDone(true)
            } else {
                setMatchTimerDone(false)
            }
        }
        // Run immediately on mount to catch already-expired timers
        tick()
        matchTimerRef.current = setInterval(tick, 5000)
        return () => clearInterval(matchTimerRef.current)
    }, [tournament?.status, tournament?.startedAt])

    // ── 20-min dispute window after first claim submitted ────
    useEffect(() => {
        if (!tournament?.firstClaimAt) return
        if (tournament?.status !== 'inprogress') return

        const deadline = tournament.firstClaimAt + 20 * 60 * 1000
        const left = deadline - Date.now()

        if (left <= 0) {
            setDisputeWindowOpen(false)
            clearInterval(disputeTimerRef.current)
            return
        }

        setDisputeWindowOpen(true)
        const tick = () => {
            const l = deadline - Date.now()
            setDisputeTimeLeft(Math.max(0, l))
            if (l <= 0) {
                clearInterval(disputeTimerRef.current)
                setDisputeWindowOpen(false)
            }
        }
        tick()
        disputeTimerRef.current = setInterval(tick, 1000)
        return () => clearInterval(disputeTimerRef.current)
    }, [tournament?.firstClaimAt, tournament?.status])

    // ── Auto-resolve claims when all submitted ────────────────
    useEffect(() => {
        if (!tournament || tournament.status !== 'inprogress') return
        if (!tournament.prizeMode || !players) return

        const playerList = Object.entries(players)
        if (playerList.length === 0) return

        const allClaimed = playerList.every(([, p]) => p.claim !== undefined)
        if (!allClaimed) return

        // Check for conflicts
        const claimMap = {}  // position → [uids]
        playerList.forEach(([uid, p]) => {
            if (p.claim && p.claim !== 'dnp') {
                if (!claimMap[p.claim]) claimMap[p.claim] = []
                claimMap[p.claim].push(uid)
            }
        })

        const positions = tournament.prizeMode === 'winner' ? [1] : [1, 2, 3]
        const conflicts = positions.filter(pos => (claimMap[pos] || []).length > 1)

        if (conflicts.length === 0) {
            // No conflicts — announce winners
            handleAnnounceFromClaims(claimMap)
        } else {
            // Dispute
            handleDisputeClaims(conflicts)
        }
    }, [players, tournament?.status])

    // ─────────────────────────────────────────────────────────
    //  DERIVED
    // ─────────────────────────────────────────────────────────
    const isHost       = currentUser && tournament?.hostUid === currentUser.uid
    const isParticipant = currentUser && !!players[currentUser.uid]
    const playerList   = Object.entries(players)
    const playerCount  = playerList.length
    const totalPot     = playerCount * (tournament?.entryFee || 0)
    const prizes       = tournament ? calcPrizes(totalPot, tournament.prizeMode, playerCount) : {}
    const canStart     = playerCount >= 4
    const allUids      = playerList.map(([uid]) => uid)

    // ─────────────────────────────────────────────────────────
    //  ACTIONS
    // ─────────────────────────────────────────────────────────

    // Wait expiry — refund all if < 4 players
    const handleWaitExpiry = useCallback(async () => {
        if (!tournament || tournament.status !== 'waiting') return
        if (playerCount >= 4) return  // enough players, host decides
        const db = getDB()
        await update(ref(db, `tournaments/${tid}/meta`), { status: 'cancelled', cancelReason: 'Not enough players' })
        await refundAll('Tournament cancelled — not enough players joined in time.')
    }, [tournament, playerCount, tid])

    // Refund all players
    const refundAll = useCallback(async (reason) => {
        const db = getDB()
        await Promise.all(playerList.map(async ([uid, p]) => {
            const balRef = ref(db, `users/${uid}/balance`)
            const snap = await get(balRef)
            const current = snap.val()?.diamonds ?? 0
            await set(balRef, { diamonds: current + tournament.entryFee })
            await push(ref(db, `users/${uid}/history`), {
                type: 'refund', amount: tournament.entryFee,
                description: reason, timestamp: Date.now(),
            })
            await notifyUser(uid, `💎 *Refund Issued*\n${reason}\n+${tournament.entryFee} 💎 returned to your balance.`)
        }))
    }, [playerList, tournament])

    // Host cancel
    const handleHostCancel = useCallback(async () => {
        if (!isHost) return
        setActionLoading(true)
        const db = getDB()
        await update(ref(db, `tournaments/${tid}/meta`), { status: 'cancelled', cancelReason: 'Host cancelled' })
        await refundAll('Tournament cancelled by host — full refund.')
        navigate('/')
        setActionLoading(false)
    }, [isHost, tid, refundAll, navigate])

    // Host start tournament
    const handleStart = useCallback(async () => {
        if (!isHost || !canStart) return
        setActionLoading(true)
        const db = getDB()
        await update(ref(db, `tournaments/${tid}/meta`), {
            status: 'inprogress',
            startedAt: Date.now(),
            finalPlayerCount: playerCount,
        })
        await notifyAll(allUids,
            `🏆 *Tournament Started!*\n\n` +
            `*${tournament.name}* by ${tournament.publisher}\n` +
            `🎮 ${tournament.gameType === 'pool' ? '8 Ball Pool 🎱' : 'Checkers ♟️'}\n` +
            `👥 ${playerCount} players · 💎 ${totalPot} prize pool\n\n` +
            `🎥 *Start recording your gameplay now!*\n` +
            `You have 25 minutes. Good luck! 🏆`
        )
        setActionLoading(false)
    }, [isHost, canStart, tid, playerCount, allUids, tournament, totalPot])

    // Player claim position
    const handleClaim = useCallback(async (position) => {
        if (!currentUser || !isParticipant || claimSubmitted) return
        setActionLoading(true)
        const db = getDB()
        await update(ref(db, `tournaments/${tid}/players/${currentUser.uid}`), {
            claim: position,
            claimedAt: Date.now(),
        })

        // Record firstClaimAt to start dispute window
        if (!tournament.firstClaimAt) {
            await update(ref(db, `tournaments/${tid}/meta`), { firstClaimAt: Date.now() })
        }

        setMyClaim(position)
        setClaimSubmitted(true)

        // Notify host
        const posText = position === 'dnp' ? 'Did Not Place' : `Position ${position}`
        await notifyUser(tournament.hostUid,
            `📊 *Result Claim*\n` +
            `*${players[currentUser.uid]?.displayName}* claimed: *${posText}*\n` +
            `Tournament: ${tournament.name}`
        )
        setActionLoading(false)
    }, [currentUser, isParticipant, claimSubmitted, tid, tournament, players])

    // Host declare rankings directly
    const handleHostDeclare = useCallback(async () => {
        if (!isHost) return
        const { 1: first, 2: second, 3: third } = hostRankings
        if (tournament.prizeMode === 'winner' && !first) return
        if (tournament.prizeMode === 'top3' && (!first || !second || !third)) return

        setActionLoading(true)
        const db = getDB()

        const winners = tournament.prizeMode === 'winner'
            ? { 1: first }
            : { 1: first, 2: second, 3: third }

        await update(ref(db, `tournaments/${tid}/meta`), {
            status: 'announced',
            winners,
            announcedAt: Date.now(),
            declaredByHost: true,
        })

        await notifyWinners(winners)
        setShowHostDeclare(false)
        setActionLoading(false)
    }, [isHost, hostRankings, tournament, tid])

    // Auto announce from claims
    const handleAnnounceFromClaims = useCallback(async (claimMap) => {
        if (!tournament) return
        const db = getDB()
        const winners = {}
        Object.entries(claimMap).forEach(([pos, uids]) => {
            winners[pos] = uids[0]
        })
        await update(ref(db, `tournaments/${tid}/meta`), {
            status: 'announced',
            winners,
            announcedAt: Date.now(),
            declaredByHost: false,
        })
        await notifyWinners(winners)
    }, [tournament, tid])

    // Dispute claims — creates dispute record and navigates
    const handleDisputeClaims = useCallback(async (conflictedPositions) => {
        const db = getDB()

        // Build claims map for dispute record
        const claimsMap = {}
        playerList.forEach(([uid, p]) => {
            if (p.claim !== undefined && p.claim !== null) {
                claimsMap[uid] = p.claim === 'dnp' ? 'Did Not Place' : `Position ${p.claim}`
            }
        })

        const partyNamesMap = {}
        playerList.forEach(([uid, p]) => { partyNamesMap[uid] = p.displayName })

        const evidenceDeadline = Date.now() + 20 * 60 * 1000  // 20 min

        const disputeRef = push(ref(db, 'disputes'))
        const did = disputeRef.key

        await set(disputeRef, {
            did,
            type:               'tournament',
            tournamentId:       tid,
            status:             'evidence',
            autoDispute:        true,
            depositPaid:        false,
            gameType:           tournament?.gameType,
            prizePool:          totalPot,
            parties:            allUids,
            partyNames:         partyNamesMap,
            claims:             claimsMap,
            conflictedPositions,
            evidence:           {},
            evidenceDeadline,
            createdAt:          Date.now(),
            winner:             null,
            penalisedUid:       null,
            adminNote:          '',
            resolvedAt:         null,
        })

        await update(ref(db, `tournaments/${tid}/meta`), {
            status:    'disputed',
            disputeId: did,
            disputedAt: Date.now(),
            conflictedPositions,
        })

        await notifyAll(allUids,
            `⚔️ *Tournament Disputed*\n\n` +
            `Conflicting claims in *${tournament?.name}*.\n\n` +
            `📹 You have *20 minutes* to submit your evidence link.\n` +
            `⚠️ No evidence = decision may not favour you.\n\n` +
            `Open the app to submit evidence.`
        )

        navigate(`/dispute/${did}`)
    }, [tournament, tid, allUids, playerList, totalPot, navigate])

    // Notify winners
    const notifyWinners = useCallback(async (winners) => {
        const db = getDB()
        const prz = calcPrizes(totalPot, tournament.prizeMode, playerCount)
        await Promise.all(Object.entries(winners).map(async ([pos, uid]) => {
            const payout = prz[pos] || 0
            const posText = pos === '1' ? '🥇 1st Place' : pos === '2' ? '🥈 2nd Place' : '🥉 3rd Place'
            await notifyUser(uid,
                `🏆 *You placed ${posText}!*\n\n` +
                `Tournament: *${tournament.name}*\n` +
                `💎 Prize: *${payout} diamonds*\n\n` +
                `⏳ Payout releases in 5 minutes if no dispute is raised.`
            )
        }))
        // Notify losers
        const winnerUids = Object.values(winners)
        const losers = allUids.filter(uid => !winnerUids.includes(uid))
        await notifyAll(losers,
            `🏁 *Tournament Results Announced*\n\n` +
            `Tournament: *${tournament.name}*\n` +
            `Better luck next time! 💪\n\n` +
            `Results are being reviewed for 5 minutes before payout.`
        )
    }, [tournament, totalPot, playerCount, allUids])

    // Auto settle after 5 min payout window
    const handleAutoSettle = useCallback(async () => {
        if (!tournament || tournament.status !== 'announced') return
        const db = getDB()

        const freshSnap = await get(ref(db, `tournaments/${tid}/meta/settled`))
        if (freshSnap.val() === true) return
        await update(ref(db, `tournaments/${tid}/meta`), { settled: true })

        const prz = calcPrizes(totalPot, tournament.prizeMode, playerCount)

        // Pay winners
        await Promise.all(Object.entries(tournament.winners).map(async ([pos, uid]) => {
            const payout = prz[pos] || 0
            if (!payout || !uid) return
            const balRef = ref(db, `users/${uid}/balance`)
            const snap = await get(balRef)
            const current = snap.val()?.diamonds ?? 0
            await set(balRef, { diamonds: current + payout })
            await push(ref(db, `users/${uid}/history`), {
                type: 'win', amount: payout,
                description: `Tournament win — ${tournament.name} (Position ${pos})`,
                gameType: tournament.gameType, timestamp: Date.now(),
            })
            // Update profile level
            const profileRef = ref(db, `users/${uid}/profile`)
            const profile = (await get(profileRef)).val() || {}
            const newWins = (profile.wins || 0) + 1
            await update(profileRef, { wins: newWins, level: Math.floor(newWins / 5) + 1 })
        }))

        // Host bonus if 8 players + no dispute
        if (playerCount === 8 && tournament.status !== 'disputed') {
            const bonus = hostBonus(tournament.entryFee, playerCount)
            if (bonus > 0) {
                const hBalRef = ref(db, `users/${tournament.hostUid}/balance`)
                const hSnap = await get(hBalRef)
                const hCurrent = hSnap.val()?.diamonds ?? 0
                await set(hBalRef, { diamonds: hCurrent + bonus })
                await push(ref(db, `users/${tournament.hostUid}/history`), {
                    type: 'host_bonus', amount: bonus,
                    description: `Host bonus — ${tournament.name} (8 players, no dispute)`,
                    timestamp: Date.now(),
                })
                await notifyUser(tournament.hostUid,
                    `🎁 *Host Bonus!*\n+${bonus} 💎 host bonus for running a full 8-player tournament with no disputes!`
                )
            }
        }

        await update(ref(db, `tournaments/${tid}/meta`), {
            status: 'settled', settledAt: Date.now(),
        })

        // Clear inMatch presence
        await Promise.all(allUids.map(uid =>
            update(ref(db, `users/${uid}/presence`), { inMatch: false, availableToChallenge: false })
        ))

        await notifyAll(allUids,
            `✅ *Tournament Settled!*\n*${tournament.name}* payouts have been released. Check your balance! 💎`
        )
    }, [tournament, tid, totalPot, playerCount, allUids])

    // ─────────────────────────────────────────────────────────
    //  DISPUTE MODAL — reusable across announced + inprogress
    // ─────────────────────────────────────────────────────────
    const DisputeModal = () => !showDisputeModal ? null : (
        <div style={s.overlayInline}>
            <div style={s.disputeModalCard}>
                <div style={{ fontSize: 36, textAlign: 'center' }}>🚩</div>
                <div style={s.disputeModalTitle}>Raise a Dispute?</div>
                <p style={s.disputeModalBody}>
                    You'll pay <strong>💎 10</strong> deposit.
                    Refunded if your claim is correct. If wrong, deposit is forfeited and a penalty may apply.
                </p>
                <p style={s.disputeModalBody}>
                    🎥 You'll need to submit a <strong>YouTube or Google Drive</strong> recording link as evidence.
                </p>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button style={s.outlineBtn} onClick={() => setShowDisputeModal(false)} disabled={disputeDepositing}>Cancel</button>
                    <button
                        style={{ ...s.goldBtn, background: '#dc2626', flex: 2 }}
                        disabled={disputeDepositing}
                        onClick={async () => {
                            setDisputeDepositing(true)
                            try {
                                const db = getDB()
                                const balSnap = await get(ref(db, `users/${currentUser.uid}/balance`))
                                const bal = balSnap.val()?.diamonds ?? 0
                                if (bal < 10) { alert('Not enough 💎. Need 10 diamonds.'); setDisputeDepositing(false); return }
                                await set(ref(db, `users/${currentUser.uid}/balance`), { diamonds: bal - 10 })
                                await push(ref(db, `users/${currentUser.uid}/history`), {
                                    type: 'entry', amount: -10,
                                    description: 'Dispute deposit — refunded if claim is correct',
                                    timestamp: Date.now(),
                                })
                                const claimsMap = {}
                                playerList.forEach(([uid, p]) => {
                                    if (p.claim !== undefined && p.claim !== null)
                                        claimsMap[uid] = p.claim === 'dnp' ? 'Did Not Place' : `Position ${p.claim}`
                                })
                                const partyNamesMap = {}
                                playerList.forEach(([uid, p]) => { partyNamesMap[uid] = p.displayName })
                                const disputeRef = push(ref(db, 'disputes'))
                                const did = disputeRef.key
                                await set(disputeRef, {
                                    did, type: 'tournament', tournamentId: tid,
                                    status: 'evidence', autoDispute: false, depositPaid: true,
                                    gameType: tournament?.gameType, prizePool: totalPot,
                                    parties: allUids, partyNames: partyNamesMap, claims: claimsMap,
                                    evidence: {}, evidenceDeadline: Date.now() + 20 * 60 * 1000,
                                    createdAt: Date.now(), winner: null, penalisedUid: null, adminNote: '', resolvedAt: null,
                                })
                                await update(ref(db, `tournaments/${tid}/meta`), { status: 'disputed', disputeId: did, disputedAt: Date.now() })
                                await notifyAll(allUids, `⚔️ *Tournament Disputed*\n*${tournament?.name}*\n📹 Submit evidence within 20 min.`)
                                navigate(`/dispute/${did}`)
                            } catch(e) { alert('Failed. Try again.') }
                            setDisputeDepositing(false)
                        }}
                    >
                        {disputeDepositing ? 'Processing…' : 'Pay 💎 10 & Dispute'}
                    </button>
                </div>
            </div>
        </div>
    )

    // ─────────────────────────────────────────────────────────
    //  RENDER HELPERS
    // ─────────────────────────────────────────────────────────

    const StatusBar = () => {
        const stages = ['waiting', 'inprogress', 'announced', 'settled']
        const stageLabels = { waiting: 'Open', inprogress: 'In Progress', announced: 'Results', settled: 'Settled' }
        const current = stages.indexOf(tournament?.status)
        return (
            <div style={s.statusBar}>
                {stages.map((stage, i) => {
                    const done   = i < current
                    const active = i === current
                    return (
                        <React.Fragment key={stage}>
                            <div style={s.stageWrap}>
                                <div style={done ? s.dotDone : active ? s.dotActive : s.dotLocked}>
                                    {done ? '✓' : active ? '' : '🔒'}
                                </div>
                                <div style={done || active ? s.stageLabel : s.stageLabelDim}>
                                    {stageLabels[stage]}
                                </div>
                            </div>
                            {i < stages.length - 1 && (
                                <div style={i < current ? s.connDone : s.connPending} />
                            )}
                        </React.Fragment>
                    )
                })}
            </div>
        )
    }

    const PlayerSlots = () => (
        <div style={s.slotsGrid}>
            {playerList.map(([uid, p]) => (
                <div key={uid} style={s.slot}>
                    <div style={s.slotAvatar}>{initials(p.displayName)}</div>
                    <div style={s.slotName}>{p.displayName}</div>
                    {uid === tournament?.hostUid && <div style={s.hostBadge}>HOST</div>}
                    {p.claim !== undefined && (
                        <div style={s.claimBadge}>
                            {p.claim === 'dnp' ? 'DNP' : posLabel(p.claim)}
                        </div>
                    )}
                </div>
            ))}
            {/* Empty slots */}
            {Array.from({ length: Math.max(0, (tournament?.maxPlayers || 8) - playerCount) }).map((_, i) => (
                <div key={`empty-${i}`} style={s.slotEmpty}>
                    <div style={s.slotAvatarEmpty}>?</div>
                    <div style={s.slotNameDim}>Waiting…</div>
                </div>
            ))}
        </div>
    )

    const PrizeBanner = () => (
        <div style={s.prizeBanner}>
            <div style={s.prizeTitle}>💎 Prize Pool</div>
            <div style={s.prizePot}>{totalPot}</div>
            <div style={s.prizeMode}>
                {tournament?.prizeMode === 'winner' ? 'Winner Takes All' : 'Top 3 Split'}
            </div>
            {tournament?.prizeMode === 'top3' && (
                <div style={s.prizeBreakdown}>
                    <span>🥇 {prizes[1] || 0}</span>
                    <span>🥈 {prizes[2] || 0}</span>
                    <span>🥉 {prizes[3] || 0}</span>
                </div>
            )}
            <div style={s.prizeNote}>15% platform fee · Winner gets {Math.round((1 - PLATFORM_FEE) * 100)}% of pot</div>
        </div>
    )

    // ─────────────────────────────────────────────────────────
    //  RENDER
    // ─────────────────────────────────────────────────────────
    if (!authReady || loading) {
        return <div style={s.root}><div style={s.centered}><div style={s.spinner} /><div style={s.loadText}>Loading tournament…</div></div></div>
    }
    if (error) {
        return <div style={s.root}><div style={s.centered}><div style={s.errorBig}>{error}</div><button style={s.greenBtn} onClick={() => navigate('/')}>← Home</button></div></div>
    }

    // Firebase meta is missing but MongoDB knows about this tournament — show join card
    if (!tournament && mongoTournament) {
        const isOpenInMongo = mongoTournament.status === 'open'
        const myMongoId     = getMongoId()
        const alreadyJoined = mongoTournament.players?.some(
            p => p.userId?._id === myMongoId || p.userId?.toString?.() === myMongoId
        )
        return (
            <div style={s.root}><div style={s.page}>
                <div style={s.header}>
                    <button style={s.backIcon} onClick={() => navigate('/')}>←</button>
                    <span style={s.headerTitle}>Tournament Lobby</span>
                </div>
                <div style={s.tournCard}>
                    <div style={s.tournName}>{mongoTournament.title}</div>
                    <div style={s.tournPublisher}>by {mongoTournament.host?.username}</div>
                    <div style={s.tournMeta}>
                        <span>{mongoTournament.game === 'pool' ? '🎱 8 Ball Pool' : '♟️ Checkers'}</span>
                        <span>💎 {mongoTournament.entryFee} entry</span>
                        <span>👥 {mongoTournament.players?.length || 0}/{mongoTournament.maxPlayers}</span>
                    </div>
                </div>
                <div style={s.card}>
                    {isOpenInMongo && !alreadyJoined ? (
                        <>
                            <div style={s.waitingTitle}>⚔️ Join this Tournament</div>
                            <p style={s.instructionText}>
                                Entry fee: <strong>💎 {mongoTournament.entryFee}</strong>
                            </p>
                            <div style={s.recordReminder}>
                                🎥 <strong>Record your gameplay!</strong> Start recording before the tournament begins.
                            </div>
                            <button
                                style={s.goldBtn}
                                disabled={actionLoading}
                                onClick={async () => {
                                    if (!currentUser) { navigate('/account'); return }
                                    setActionLoading(true)
                                    try {
                                        const db = getDB()
                                        // 1. Join in MongoDB — deducts entry fee atomically
                                        await tournamentsAPI.join(tid)
                                        // 2. Write player to Firebase — backend will have synced meta already
                                        const profSnap = await get(ref(db, `users/${currentUser.uid}/profile`))
                                        const dName = profSnap.val()?.displayName || currentUser.email?.split('@')[0] || 'Player'
                                        await set(ref(db, `tournaments/${tid}/players/${currentUser.uid}`), {
                                            uid: currentUser.uid, displayName: dName,
                                            joinedAt: Date.now(), isHost: false, paid: true, claim: null,
                                        })
                                        // Firebase onValue will now pick up the populated node
                                        window.location.reload()
                                    } catch(e) {
                                        setError(e.message || 'Failed to join. Try again.')
                                    }
                                    setActionLoading(false)
                                }}
                            >
                                {actionLoading ? 'Joining…' : `✅ Join & Deposit 💎 ${mongoTournament.entryFee}`}
                            </button>
                        </>
                    ) : alreadyJoined ? (
                        <div style={s.waitingTitle}>✅ You have joined this tournament. Waiting for lobby to load…</div>
                    ) : (
                        <div style={s.waitingTitle}>This tournament is no longer open for joining.</div>
                    )}
                </div>
            </div></div>
        )
    }

    // Not host, not participant, and tournament is past the open/waiting stage
    if (!isHost && !isParticipant && tournament?.status !== 'waiting') {
        return <div style={s.root}><div style={s.centered}><div style={s.errorBig}>⛔ You're not part of this tournament.</div><button style={s.greenBtn} onClick={() => navigate('/')}>← Home</button></div></div>
    }

    // ── CANCELLED ─────────────────────────────────────────────
    if (tournament?.status === 'cancelled') {
        return (
            <div style={s.root}><div style={s.page}>
                <div style={s.header}><button style={s.backIcon} onClick={() => navigate('/')}>←</button><span style={s.headerTitle}>Tournament Cancelled</span></div>
                <div style={s.centered}>
                    <div style={{ fontSize: 56 }}>❌</div>
                    <div style={s.bigLabel}>Tournament Cancelled</div>
                    <div style={s.subLabel}>{tournament.cancelReason}</div>
                    <div style={s.subLabel}>Full refund issued to all players.</div>
                    <button style={s.greenBtn} onClick={() => navigate('/')}>Back to Home</button>
                </div>
            </div></div>
        )
    }

    // ── DISPUTED ──────────────────────────────────────────────
    if (tournament?.status === 'disputed') {
        return (
            <div style={s.root}><div style={s.page}>
                <div style={s.header}><button style={s.backIcon} onClick={() => navigate('/')}>←</button><span style={s.headerTitle}>Under Review</span></div>
                <div style={s.card}>
                    <div style={{ fontSize: 48, textAlign: 'center' }}>⚠️</div>
                    <div style={s.bigLabel}>Results Under Review</div>
                    <div style={s.disputeBox}>
                        <p style={s.disputeText}>Conflicting position claims were submitted for this tournament.</p>
                        <p style={s.disputeText}>🕒 Resolution may take up to <strong>24 hours</strong>.</p>
                        <p style={s.disputeText}>📹 You may be asked to submit your <strong>gameplay recording</strong>.</p>
                        <p style={{ ...s.disputeText, color: '#f87171' }}>⚠️ False claims are penalised <strong>{DISPUTE_PENALTY} 💎</strong>.</p>
                    </div>
                    <button style={s.outlineBtn} onClick={() => navigate('/')}>Back to Home</button>
                </div>
            </div></div>
        )
    }

    // ── SETTLED ───────────────────────────────────────────────
    if (tournament?.status === 'settled') {
        const myPos = Object.entries(tournament.winners || {}).find(([, uid]) => uid === currentUser?.uid)
        const iWon  = !!myPos
        const myPayout = myPos ? prizes[myPos[0]] : 0
        return (
            <div style={s.root}><div style={s.page}>
                <div style={s.header}><button style={s.backIcon} onClick={() => navigate('/')}>←</button><span style={s.headerTitle}>Tournament Settled</span></div>
                <div style={s.card}>
                    <div style={{ fontSize: 56, textAlign: 'center' }}>{iWon ? '🏆' : '🏁'}</div>
                    <div style={{ ...s.bigLabel, color: iWon ? '#ffd700' : '#e8f5e9' }}>
                        {iWon ? `You placed ${posLabel(Number(myPos[0]))}!` : 'Tournament Complete'}
                    </div>
                    {iWon && <div style={s.payoutBadge}>+{myPayout} 💎 added to your balance</div>}
                    {/* Winners list */}
                    <div style={s.winnersCard}>
                        {Object.entries(tournament.winners || {}).map(([pos, uid]) => {
                            const p = players[uid]
                            return (
                                <div key={pos} style={s.winnerRow}>
                                    <span style={s.winnerPos}>{posLabel(Number(pos))}</span>
                                    <span style={s.winnerName}>{p?.displayName || uid}</span>
                                    <span style={s.winnerPrize}>+{prizes[pos]} 💎</span>
                                </div>
                            )
                        })}
                    </div>
                    <button style={s.greenBtn} onClick={() => navigate('/')}>Back to Home</button>
                </div>
            </div></div>
        )
    }

    // ── ANNOUNCED — payout pending ────────────────────────────
    if (tournament?.status === 'announced') {
        const myPos = Object.entries(tournament.winners || {}).find(([, uid]) => uid === currentUser?.uid)
        return (
            <div style={s.root}><div style={s.page}>
                <div style={s.header}><button style={s.backIcon} onClick={() => navigate('/')}>←</button><span style={s.headerTitle}>Results Announced</span></div>
                <PrizeBanner />
                <div style={s.card}>
                    <div style={s.waitingTitle}>🏆 Winners</div>
                    <div style={s.winnersCard}>
                        {Object.entries(tournament.winners || {}).map(([pos, uid]) => {
                            const p = players[uid]
                            return (
                                <div key={pos} style={s.winnerRow}>
                                    <span style={s.winnerPos}>{posLabel(Number(pos))}</span>
                                    <span style={s.winnerName}>{p?.displayName || uid}</span>
                                    <span style={s.winnerPrize}>{prizes[pos]} 💎</span>
                                </div>
                            )
                        })}
                    </div>
                    {payoutTimeLeft !== null && (
                        <div style={s.payoutTimer}>
                            <span style={s.payoutLabel}>Payout releases in</span>
                            <span style={{ ...s.expiryTimer, color: '#ffd700' }}>{fmtTime(payoutTimeLeft)}</span>
                        </div>
                    )}
                    {myPos
                        ? <div style={s.myResultGood}>You placed {posLabel(Number(myPos[0]))} — 💎 {prizes[myPos[0]]} incoming!</div>
                        : <div style={s.myResultBad}>You did not place this time. Better luck next tournament!</div>
                    }
                    {payoutTimeLeft !== null && payoutTimeLeft > 0 && !isHost && (
                        <div style={{ marginTop: 16, borderTop: '1px solid #1a2a3a', paddingTop: 14 }}>
                            <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>
                                Disagree with the result? You can raise a dispute before payout releases.
                            </p>
                            <button
                                style={{ ...s.disputeBtn, width: '100%' }}
                                onClick={() => setShowDisputeModal(true)}
                                disabled={actionLoading || disputeDepositing}
                            >
                                🚩 Raise Dispute — Pay 💎 10
                            </button>
                        </div>
                    )}
                </div>
                <DisputeModal />
            </div></div>
        )
    }

    // ── MAIN LOBBY ────────────────────────────────────────────
    return (
        <div style={s.root}>
            <div style={s.page}>

                {/* Header */}
                <div style={s.header}>
                    <button style={s.backIcon} onClick={() => navigate('/')}>←</button>
                    <span style={s.headerTitle}>Tournament Lobby</span>
                    {isHost && <span style={s.hostTag}>HOST</span>}
                </div>

                {error && <div style={s.errorMsg}>{error}</div>}

                {/* Tournament info */}
                <div style={s.tournCard}>
                    <div style={s.tournName}>{tournament?.name}</div>
                    <div style={s.tournPublisher}>{tournament?.publisher}</div>
                    <div style={s.tournMeta}>
                        <span>{tournament?.gameType === 'pool' ? '🎱 8 Ball Pool' : '♟️ Checkers'}</span>
                        <span>💎 {tournament?.entryFee} entry</span>
                        <span>👥 {playerCount}/{tournament?.maxPlayers}</span>
                    </div>
                    {/* Share link — subtle */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                        <button style={{ background: 'none', border: '1px solid #1a2a3a', borderRadius: 6, padding: '4px 10px', color: '#4a7aaa', fontSize: 11, cursor: 'pointer' }}
                            onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/tournament/${tid}`)
                                setCopiedKey('tid')
                                setTimeout(() => setCopiedKey(null), 2000)
                            }}>
                            {copiedKey === 'tid' ? '✅ Link Copied!' : '🔗 Share Lobby'}
                        </button>
                    </div>
                    {tournament?.externalTournamentId && (
                        playerCount >= 4 ? (
                            <div style={{ background: '#0d2a1a', border: '1px solid #1a3a2a', borderRadius: 8, padding: '10px 12px', marginTop: 8 }}>
                                <div style={{ fontSize: 11, color: '#4ade80', fontWeight: 600, marginBottom: 4 }}>🎮 Game Tournament ID</div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#ffd700', fontSize: 22, fontWeight: 700, letterSpacing: 1 }}>{tournament.externalTournamentId}</span>
                                    <button style={s.copyBtn} onClick={() => { navigator.clipboard.writeText(tournament.externalTournamentId); setCopiedKey('ext'); setTimeout(() => setCopiedKey(null), 2000) }}>{copiedKey === 'ext' ? '✅ Copied!' : '📋 Copy'}</button>
                                </div>
                                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Enter this ID in the game app to join the right room</div>
                            </div>
                        ) : (
                            <div style={{ background: '#1a1a0d', border: '1px solid #2a2a1a', borderRadius: 8, padding: '10px 12px', marginTop: 8, textAlign: 'center' }}>
                                <div style={{ fontSize: 12, color: '#fbbf24' }}>🔒 Game Tournament ID</div>
                                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Visible once 4 players have joined ({playerCount}/4)</div>
                            </div>
                        )
                    )}
                </div>

                {/* Status bar */}
                <div style={s.card}><StatusBar /></div>

                {/* Prize banner */}
                <PrizeBanner />

                {/* Players */}
                <div style={s.card}>
                    <div style={s.sectionHead}>
                        <span style={s.sectionTitle}>Players ({playerCount}/{tournament?.maxPlayers})</span>
                        {tournament?.status === 'waiting' && waitTimeLeft !== null && (
                            <span style={{ color: waitTimeLeft < 60000 ? '#f87171' : '#4ade80', fontSize: 13, fontWeight: 700 }}>
                                Lobby closes in {fmtTime(waitTimeLeft)}
                            </span>
                        )}
                    </div>
                    <PlayerSlots />
                </div>

                {/* ── WAITING STATUS ACTIONS ── */}
                {/* Non-participant join card */}
                {tournament?.status === 'waiting' && !isHost && !isParticipant && currentUser && (
                    <div style={s.card}>
                        <div style={s.waitingTitle}>⚔️ Join this Tournament</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 13, color: '#4a7a50' }}>
                            <span>Entry fee</span><strong style={{ color: '#4ade80' }}>💎 {tournament.entryFee}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 13, color: '#4a7a50', borderBottom: '1px solid #0a2a0c', marginBottom: 12 }}>
                            <span>Players</span><strong style={{ color: '#e8f5e9' }}>{playerCount}/{tournament.maxPlayers}</strong>
                        </div>
                        <div style={s.recordReminder}>
                            🎥 <strong>Record your gameplay!</strong> Start recording before the tournament begins. Recordings protect you in case of a dispute.
                        </div>
                        <button
                            style={s.goldBtn}
                            disabled={actionLoading}
                            onClick={async () => {
                                setActionLoading(true)
                                try {
                                    const db = getDB()

                                    // 1. Deduct from MongoDB wallet (authoritative) + syncs Firebase balance
                                    if (!tournament.mongoId) throw new Error('Tournament missing MongoDB ID — please rejoin via a new tournament.')
                                    await tournamentsAPI.join(tournament.mongoId)

                                    // 2. Add player to Firebase RTDB lobby
                                    const profSnap = await get(ref(db, `users/${currentUser.uid}/profile`))
                                    const dName = profSnap.val()?.displayName || currentUser.email?.split('@')[0] || 'Player'
                                    await set(ref(db, `tournaments/${tid}/players/${currentUser.uid}`), {
                                        uid: currentUser.uid, displayName: dName,
                                        joinedAt: Date.now(), isHost: false, paid: true, claim: null,
                                    })
                                    await push(ref(db, `users/${currentUser.uid}/history`), {
                                        type: 'entry', amount: -tournament.entryFee,
                                        description: `Tournament entry — ${tournament.name}`,
                                        gameType: tournament.gameType, timestamp: Date.now(),
                                    })
                                    notifyUser(tournament.hostUid,
                                        `🎉 *New Player Joined!*\n*${dName}* joined *${tournament.name}*\n👥 ${playerCount + 1}/${tournament.maxPlayers} players`
                                    )
                                } catch(e) { alert(e.message || 'Failed to join. Try again.') }
                                setActionLoading(false)
                            }}
                        >
                            {actionLoading ? 'Joining…' : `✅ Join & Deposit 💎 ${tournament.entryFee}`}
                        </button>
                    </div>
                )}

                {tournament?.status === 'waiting' && !isHost && !isParticipant && !currentUser && (
                    <div style={s.card}>
                        <div style={s.waitingTitle}>Sign in to join</div>
                        <button style={s.goldBtn} onClick={() => navigate('/account')}>Sign In →</button>
                    </div>
                )}

                {tournament?.status === 'waiting' && (
                    <div style={s.card}>
                        {isHost ? (
                            <>
                                <div style={s.waitingTitle}>
                                    {canStart
                                        ? `✅ ${playerCount} players joined — ready to start!`
                                        : `⏳ Waiting for players… (need at least 4)`}
                                </div>
                                <div style={s.recordReminder}>
                                    🎥 <strong>Remind players to record!</strong> Once you start, all participants
                                    should begin screen recording. Recordings protect everyone in case of a dispute.
                                </div>
                                <button
                                    style={canStart ? s.goldBtn : s.goldBtnDisabled}
                                    onClick={handleStart}
                                    disabled={!canStart || actionLoading}
                                >
                                    {actionLoading ? 'Starting…' : `🏁 Start Tournament (${playerCount} players)`}
                                </button>
                                <button
                                    style={s.dangerOutlineBtn}
                                    onClick={handleHostCancel}
                                    disabled={actionLoading}
                                >Cancel Tournament — Full Refund to All</button>
                            </>
                        ) : (
                            <>
                                <div style={s.waitingTitle}>⏳ Waiting for host to start…</div>
                                <div style={s.recordReminder}>
                                    🎥 <strong>Get ready to record!</strong> Prepare your screen recording
                                    before the tournament starts.
                                </div>
                                {waitTimeLeft !== null && (
                                    <div style={s.expiryRow}>
                                        <span style={s.expiryLabel}>Lobby closes in</span>
                                        <span style={{
                                            ...s.expiryTimer,
                                            color: waitTimeLeft < 60000 ? '#f87171' : '#ffd700'
                                        }}>{fmtTime(waitTimeLeft)}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ── IN PROGRESS ACTIONS ── */}
                {tournament?.status === 'inprogress' && (
                    <>
                        {/* Progress timer */}
                        <div style={s.progressCard}>
                            <div style={s.progressLabel}>Tournament In Progress</div>
                            {progressTimeLeft !== null && (
                                <div style={s.progressTimer}>{fmtTime(progressTimeLeft)}</div>
                            )}
                            <div style={s.progressSub}>remaining · results accepted anytime</div>
                        </div>

                        {/* Recording reminder */}
                        <div style={s.recordCard}>
                            <span style={{ fontSize: 20 }}>🎥</span>
                            <div>
                                <div style={s.recordTitle}>Keep Recording!</div>
                                <div style={s.recordBody}>
                                    Keep your screen recording running throughout the tournament.
                                    If a dispute is raised, both parties may be asked to submit recordings.
                                    Unnecessary disputes are penalised <strong>{DISPUTE_PENALTY} 💎</strong>.
                                </div>
                            </div>
                        </div>

                        {/* Player claims their position */}
                        {isParticipant && !isHost && (
                            <div style={s.card}>
                                <div style={s.waitingTitle}>
                                    {claimSubmitted
                                        ? `✅ You claimed: ${myClaim === 'dnp' ? 'Did Not Place' : posLabel(myClaim)}`
                                        : '🏁 Claim Your Finish Position'}
                                </div>
                                {!matchTimerDone ? (
                                    <p style={{ ...s.instructionText, color: '#3a5a3c', textAlign: 'center' }}>
                                        ⏳ Claim buttons unlock after the 15-minute match timer ends.
                                    </p>
                                ) : !claimSubmitted ? (
                                    <>
                                        <p style={s.instructionText}>
                                            Select your finish position. Claims are compared — if everyone agrees, payout is automatic.
                                            If there's a conflict, it goes to review.
                                        </p>
                                        <div style={s.claimGrid}>
                                            {(tournament.prizeMode === 'winner'
                                                ? [{ pos: 1, label: '🥇 I Won' }]
                                                : [
                                                    { pos: 1, label: '🥇 1st Place' },
                                                    { pos: 2, label: '🥈 2nd Place' },
                                                    { pos: 3, label: '🥉 3rd Place' },
                                                ]
                                            ).map(({ pos, label }) => (
                                                <button
                                                    key={pos}
                                                    style={s.claimBtn}
                                                    onClick={() => handleClaim(pos)}
                                                    disabled={actionLoading}
                                                >{label}</button>
                                            ))}
                                            <button
                                                style={s.claimBtnDnp}
                                                onClick={() => handleClaim('dnp')}
                                                disabled={actionLoading}
                                            >I Did Not Place</button>
                                        </div>
                                    </>
                                ) : (
                                    <p style={s.instructionText}>
                                        Waiting for other players to submit their claims. Auto-settle when all agree.
                                    </p>
                                )}

                                {/* Dispute button — within 20-min window after first claim */}
                                {matchTimerDone && disputeWindowOpen && claimSubmitted && (
                                    <div style={s.disputeCard}>
                                        <div style={s.disputeTimerRow}>
                                            <span style={s.disputeLabel}>⚠️ Dispute window</span>
                                            <span style={{
                                                fontSize: 18, fontWeight: 800,
                                                color: disputeTimeLeft < 60000 ? '#f87171' : '#fbbf24',
                                                fontVariantNumeric: 'tabular-nums',
                                            }}>{fmtTime(disputeTimeLeft)}</span>
                                        </div>
                                        <p style={s.disputeNote}>
                                            Disagree with someone's claim? Raise a dispute.
                                            Costs <strong>💎 10</strong> deposit — refunded if your claim is correct.
                                        </p>
                                        <button
                                            style={s.disputeBtn}
                                            onClick={() => setShowDisputeModal(true)}
                                        >
                                            🚩 Raise Dispute — Pay 💎 10
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Dispute confirm modal */}
                        {showDisputeModal && (
                            <div style={s.overlayInline}>
                                <div style={s.disputeModalCard}>
                                    <div style={{ fontSize: 36, textAlign: 'center' }}>🚩</div>
                                    <div style={s.disputeModalTitle}>Raise a Dispute?</div>
                                    <p style={s.disputeModalBody}>
                                        You'll pay <strong>💎 10</strong> deposit.
                                        Refunded if your claim is correct. If wrong, deposit is forfeited and a penalty may apply.
                                    </p>
                                    <p style={s.disputeModalBody}>
                                        🎥 You'll need to submit a <strong>YouTube or Google Drive</strong> recording link as evidence.
                                    </p>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <button style={s.outlineBtn} onClick={() => setShowDisputeModal(false)} disabled={disputeDepositing}>Cancel</button>
                                        <button
                                            style={{ ...s.goldBtn, background: '#dc2626', flex: 2 }}
                                            disabled={disputeDepositing}
                                            onClick={async () => {
                                                setDisputeDepositing(true)
                                                try {
                                                    const db = getDB()
                                                    const balSnap = await get(ref(db, `users/${currentUser.uid}/balance`))
                                                    const bal = balSnap.val()?.diamonds ?? 0
                                                    if (bal < 10) { alert('Not enough 💎. Need 10 diamonds.'); setDisputeDepositing(false); return }
                                                    await set(ref(db, `users/${currentUser.uid}/balance`), { diamonds: bal - 10 })
                                                    await push(ref(db, `users/${currentUser.uid}/history`), {
                                                        type: 'entry', amount: -10,
                                                        description: 'Dispute deposit — refunded if claim is correct',
                                                        timestamp: Date.now(),
                                                    })
                                                    // Build claims and create dispute
                                                    const claimsMap = {}
                                                    playerList.forEach(([uid, p]) => {
                                                        if (p.claim !== undefined && p.claim !== null)
                                                            claimsMap[uid] = p.claim === 'dnp' ? 'Did Not Place' : `Position ${p.claim}`
                                                    })
                                                    const partyNamesMap = {}
                                                    playerList.forEach(([uid, p]) => { partyNamesMap[uid] = p.displayName })
                                                    const disputeRef = push(ref(db, 'disputes'))
                                                    const did = disputeRef.key
                                                    await set(disputeRef, {
                                                        did, type: 'tournament', tournamentId: tid,
                                                        status: 'evidence', autoDispute: false, depositPaid: true,
                                                        gameType: tournament?.gameType, prizePool: totalPot,
                                                        parties: allUids, partyNames: partyNamesMap, claims: claimsMap,
                                                        evidence: {}, evidenceDeadline: Date.now() + 20 * 60 * 1000,
                                                        createdAt: Date.now(), winner: null, penalisedUid: null, adminNote: '', resolvedAt: null,
                                                    })
                                                    await update(ref(db, `tournaments/${tid}/meta`), { status: 'disputed', disputeId: did, disputedAt: Date.now() })
                                                    await notifyAll(allUids, `⚔️ *Tournament Disputed*\n*${tournament?.name}*\n📹 Submit evidence within 20 min.`)
                                                    navigate(`/dispute/${did}`)
                                                } catch(e) { alert('Failed. Try again.') }
                                                setDisputeDepositing(false)
                                            }}
                                        >
                                            {disputeDepositing ? 'Processing…' : 'Pay 💎 10 & Dispute'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Host declare rankings */}
                        {isHost && (
                            <div style={s.card}>
                                <div style={s.waitingTitle}>📊 Declare Results</div>
                                <p style={s.instructionText}>
                                    You can declare the final rankings directly, or wait for players to submit their claims.
                                    Your declaration overrides all claims and settles immediately.
                                </p>

                                {!showHostDeclare ? (
                                    <button style={s.goldBtn} onClick={() => setShowHostDeclare(true)}>
                                        📣 Declare Final Rankings
                                    </button>
                                ) : (
                                    <>
                                        {(tournament.prizeMode === 'winner'
                                            ? [{ pos: 1, label: '🥇 Winner' }]
                                            : [
                                                { pos: 1, label: '🥇 1st Place' },
                                                { pos: 2, label: '🥈 2nd Place' },
                                                { pos: 3, label: '🥉 3rd Place' },
                                            ]
                                        ).map(({ pos, label }) => (
                                            <div key={pos} style={s.rankRow}>
                                                <span style={s.rankLabel}>{label}</span>
                                                <select
                                                    style={s.rankSelect}
                                                    value={hostRankings[pos]}
                                                    onChange={e => setHostRankings(prev => ({ ...prev, [pos]: e.target.value }))}
                                                >
                                                    <option value="">Select player…</option>
                                                    {playerList.map(([uid, p]) => (
                                                        <option key={uid} value={uid}>{p.displayName}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        ))}
                                        <div style={s.actionRow}>
                                            <button style={s.outlineBtn} onClick={() => setShowHostDeclare(false)}>Cancel</button>
                                            <button style={s.goldBtn} onClick={handleHostDeclare} disabled={actionLoading}>
                                                {actionLoading ? 'Confirming…' : '✅ Confirm Rankings'}
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* Players claim status */}
                                <div style={{ marginTop: 16 }}>
                                    <div style={s.sectionTitle}>Player Claims</div>
                                    {playerList.map(([uid, p]) => (
                                        <div key={uid} style={s.claimStatusRow}>
                                            <span style={s.claimStatusName}>{p.displayName}</span>
                                            <span style={p.claim !== undefined ? s.claimedTag : s.pendingTag}>
                                                {p.claim !== undefined
                                                    ? (p.claim === 'dnp' ? 'DNP' : posLabel(p.claim))
                                                    : 'Pending…'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────────────────────────────────────
const s = {
    root: { minHeight: '100vh', background: '#070a0f', color: '#e8f5e9', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    page: { maxWidth: 520, margin: '0 auto', padding: '0 16px 100px', display: 'flex', flexDirection: 'column', gap: 12 },
    centered: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16, padding: 24, textAlign: 'center' },

    header: { display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0 8px' },
    backIcon: { background: 'none', border: 'none', color: '#ffd700', fontSize: 22, cursor: 'pointer', padding: '4px 8px' },
    headerTitle: { fontSize: 18, fontWeight: 700, color: '#e8f5e9', flex: 1 },
    hostTag: { background: '#ffd70022', border: '1px solid #ffd700', color: '#ffd700', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 20, letterSpacing: '0.1em' },

    card: { background: '#0d1520', border: '1px solid #1a2a3a', borderRadius: 14, padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 12 },

    // Tournament card
    tournCard: {
        background: 'linear-gradient(135deg, #0a1a2a, #0d2040)',
        border: '1px solid #1a3a5a', borderRadius: 14, padding: '20px 16px',
        display: 'flex', flexDirection: 'column', gap: 8,
    },
    tournName: { fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: '0.04em' },
    tournPublisher: { fontSize: 13, color: '#4a7aaa' },
    tournMeta: { display: 'flex', gap: 16, fontSize: 13, color: '#6a9aca', flexWrap: 'wrap' },
    tidRow: { display: 'flex', alignItems: 'center', gap: 8, background: '#071018', borderRadius: 8, padding: '10px 12px', marginTop: 4 },
    tidLabel: { fontSize: 11, color: '#4a7aaa', letterSpacing: '0.06em' },
    tidValue: { flex: 1, fontSize: 13, fontWeight: 700, color: '#4ad4ff', letterSpacing: '0.04em', wordBreak: 'break-all' },
    copyBtn: { padding: '5px 12px', borderRadius: 6, background: '#1a3a5a', border: '1px solid #2a5a7a', color: '#4ad4ff', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },

    // Prize banner
    prizeBanner: {
        background: 'linear-gradient(135deg, #1a1000, #2a2000)',
        border: '1px solid #5a4a00', borderRadius: 14, padding: '18px 16px', textAlign: 'center',
    },
    prizeTitle: { fontSize: 11, color: '#aa9a00', letterSpacing: '0.1em', marginBottom: 4 },
    prizePot: { fontSize: 40, fontWeight: 900, color: '#ffd700', lineHeight: 1.1 },
    prizeMode: { fontSize: 13, color: '#8a7a30', marginBottom: 8 },
    prizeBreakdown: { display: 'flex', justifyContent: 'center', gap: 20, fontSize: 14, fontWeight: 700, color: '#ffd700', marginBottom: 4 },
    prizeNote: { fontSize: 11, color: '#5a5020' },

    // Status bar
    statusBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' },
    stageWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flex: 1 },
    dotDone: { width: 26, height: 26, borderRadius: '50%', background: '#ffd700', color: '#070a0f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 },
    dotActive: { width: 26, height: 26, borderRadius: '50%', background: '#1a2a3a', border: '2px solid #ffd700', color: '#ffd700', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 },
    dotLocked: { width: 26, height: 26, borderRadius: '50%', background: '#0a1520', border: '1px solid #1a2a3a', color: '#2a3a4a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 },
    stageLabel: { fontSize: 9, color: '#ffd700', textAlign: 'center', letterSpacing: '0.04em' },
    stageLabelDim: { fontSize: 9, color: '#2a3a4a', textAlign: 'center' },
    connDone: { flex: 0.3, height: 2, background: '#ffd700', borderRadius: 1, marginBottom: 16 },
    connPending: { flex: 0.3, height: 2, background: '#1a2a3a', borderRadius: 1, marginBottom: 16 },

    // Player slots
    slotsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 },
    slot: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '8px 4px', background: '#0a1a2a', borderRadius: 10, border: '1px solid #1a2a3a' },
    slotEmpty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '8px 4px', background: '#070e18', borderRadius: 10, border: '1px dashed #1a2a3a', opacity: 0.5 },
    slotAvatar: { width: 36, height: 36, borderRadius: '50%', background: '#1a3a5a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#4ad4ff' },
    slotAvatarEmpty: { width: 36, height: 36, borderRadius: '50%', background: '#0d1a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#2a3a4a' },
    slotName: { fontSize: 10, color: '#6a9aca', textAlign: 'center', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    slotNameDim: { fontSize: 10, color: '#2a3a4a' },
    hostBadge: { fontSize: 8, color: '#ffd700', background: '#ffd70022', padding: '1px 6px', borderRadius: 10, fontWeight: 800 },
    claimBadge: { fontSize: 9, color: '#4ade80', background: '#0a2a0c', padding: '2px 6px', borderRadius: 8, fontWeight: 700 },

    // Timers
    expiryRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
    expiryLabel: { fontSize: 13, color: '#4a7aaa' },
    expiryTimer: { fontSize: 28, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
    progressCard: {
        background: 'linear-gradient(135deg, #0a1a0a, #0d200d)',
        border: '1px solid #1a4a1a', borderRadius: 14, padding: '20px',
        textAlign: 'center',
    },
    progressLabel: { fontSize: 12, color: '#4ade80', letterSpacing: '0.1em', marginBottom: 4 },
    progressTimer: { fontSize: 48, fontWeight: 900, color: '#4ade80', fontVariantNumeric: 'tabular-nums', lineHeight: 1 },
    progressSub: { fontSize: 12, color: '#2a5a2a', marginTop: 4 },
    payoutTimer: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8 },
    payoutLabel: { fontSize: 13, color: '#aa9a00' },

    // Content
    sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    sectionTitle: { fontSize: 14, fontWeight: 700, color: '#e8f5e9' },
    waitingTitle: { fontSize: 15, fontWeight: 700, color: '#e8f5e9', textAlign: 'center' },
    bigLabel: { fontSize: 22, fontWeight: 800, color: '#e8f5e9', textAlign: 'center' },
    subLabel: { fontSize: 14, color: '#4a7aaa', textAlign: 'center' },
    instructionText: { fontSize: 13, color: '#4a7aaa', lineHeight: 1.6, margin: 0 },

    // Claims
    claimGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 },
    claimBtn: { padding: '14px 8px', borderRadius: 10, background: '#1a3a5a', border: '1px solid #2a5a7a', color: '#4ad4ff', fontWeight: 800, fontSize: 14, cursor: 'pointer' },
    claimBtnDnp: { padding: '12px 8px', borderRadius: 10, background: 'transparent', border: '1px solid #1a2a3a', color: '#4a6a7a', fontWeight: 600, fontSize: 13, cursor: 'pointer', gridColumn: '1 / -1' },

    // Host declare
    rankRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
    rankLabel: { fontSize: 14, fontWeight: 700, color: '#ffd700', minWidth: 90 },
    rankSelect: { flex: 1, padding: '8px 10px', borderRadius: 8, background: '#0a1520', border: '1px solid #1a2a3a', color: '#e8f5e9', fontSize: 13 },
    claimStatusRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #0d1a2a' },
    claimStatusName: { fontSize: 13, color: '#e8f5e9' },
    claimedTag: { fontSize: 11, fontWeight: 700, color: '#4ade80', background: '#0a2a0c', padding: '2px 10px', borderRadius: 10 },
    pendingTag: { fontSize: 11, color: '#4a6a7a', background: '#0a1520', padding: '2px 10px', borderRadius: 10 },

    // Winners
    winnersCard: { background: '#0a1520', borderRadius: 10, overflow: 'hidden' },
    winnerRow: { display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #0d1a2a', gap: 10 },
    winnerPos: { fontSize: 14, fontWeight: 800, minWidth: 80, color: '#ffd700' },
    winnerName: { flex: 1, fontSize: 14, color: '#e8f5e9' },
    winnerPrize: { fontSize: 13, fontWeight: 700, color: '#4ade80' },
    myResultGood: { textAlign: 'center', fontSize: 14, fontWeight: 700, color: '#4ade80', background: '#0a2a0c', borderRadius: 8, padding: '10px' },
    myResultBad: { textAlign: 'center', fontSize: 13, color: '#4a7aaa', padding: '8px' },
    payoutBadge: { background: '#1a2a00', border: '1px solid #4a5a00', borderRadius: 10, padding: '10px 20px', fontSize: 15, fontWeight: 700, color: '#ffd700', textAlign: 'center' },

    // Recording
    recordReminder: { background: '#0a1a05', border: '1px solid #2a5a1a', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#6aaa40', lineHeight: 1.5 },
    recordCard: { background: '#0a1a05', border: '1px solid #2a5a1a', borderRadius: 14, padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' },
    recordTitle: { fontSize: 13, fontWeight: 700, color: '#4ade80', marginBottom: 4 },
    recordBody: { fontSize: 12, color: '#4a7a50', lineHeight: 1.5 },

    // Dispute
    disputeBox: { background: '#1a0a0a', border: '1px solid #7f1d1d', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
    disputeText: { fontSize: 13, color: '#fca5a5', margin: 0, lineHeight: 1.5 },

    // Buttons
    greenBtn: { padding: '13px 20px', borderRadius: 12, background: '#4ade80', border: 'none', color: '#070a0f', fontWeight: 800, fontSize: 15, cursor: 'pointer', textAlign: 'center' },
    goldBtn: { padding: '13px 20px', borderRadius: 12, background: '#ffd700', border: 'none', color: '#070a0f', fontWeight: 800, fontSize: 15, cursor: 'pointer', textAlign: 'center' },
    goldBtnDisabled: { padding: '13px 20px', borderRadius: 12, background: '#3a3000', border: '1px solid #5a5000', color: '#5a5000', fontWeight: 800, fontSize: 15, cursor: 'not-allowed', textAlign: 'center' },
    outlineBtn: { padding: '12px 20px', borderRadius: 12, background: 'transparent', border: '1px solid #1a2a3a', color: '#4a7aaa', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
    dangerOutlineBtn: { padding: '11px 16px', borderRadius: 10, background: 'transparent', border: '1px solid #dc2626', color: '#f87171', fontWeight: 600, fontSize: 13, cursor: 'pointer', textAlign: 'center' },
    actionRow: { display: 'flex', gap: 10 },

    // Dispute
    disputeCard: { background: '#1a0a00', border: '1px solid #92400e', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 },
    disputeTimerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    disputeLabel: { fontSize: 12, fontWeight: 700, color: '#fbbf24' },
    disputeNote: { fontSize: 12, color: '#92400e', margin: 0, lineHeight: 1.5 },
    disputeBtn: { padding: '11px', borderRadius: 10, background: 'transparent', border: '1px solid #dc2626', color: '#f87171', fontWeight: 700, fontSize: 13, cursor: 'pointer', textAlign: 'center' },
    overlayInline: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
    disputeModalCard: { background: '#0d1520', border: '1px solid #1a2a3a', borderRadius: 16, padding: '28px 24px', width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 12 },
    disputeModalTitle: { fontSize: 18, fontWeight: 800, color: '#e8f5e9', textAlign: 'center' },
    disputeModalBody: { fontSize: 13, color: '#4a7aaa', lineHeight: 1.6, margin: 0 },

    // Misc
    errorMsg: { background: '#2a0a0a', border: '1px solid #f44336', borderRadius: 8, padding: '10px 14px', color: '#f44336', fontSize: 13 },
    errorBig: { fontSize: 18, color: '#f87171', marginBottom: 8 },
    loadText: { color: '#4a7aaa', fontSize: 14 },
    spinner: { width: 36, height: 36, borderRadius: '50%', border: '3px solid #1a2a3a', borderTop: '3px solid #ffd700' },
}
